import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  useTheme,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Autocomplete,
} from "@mui/material";
import { PersonAddAlt } from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import PublicIcon from "@mui/icons-material/Public";
import { useState, useContext } from "react";
import { motion } from "framer-motion";
import { countryPhoneCodes } from "../data/country-phone-code";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CustomSnackbar from "components/CustomSnackbar";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { encryptData } from "utils/crypto";
import { AuthContext } from "../auth/AuthContext";
import { getmessage } from "utils/i18messages";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

function SignUp() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedCode, setSelectedCode] = useState("+62");
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const googleData = location.state?.googleData;
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({
    fullName: googleData?.name,
    email: googleData?.email,
    country: "Indonesia",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (!googleData) {
      navigate("/register");
    }
  }, [googleData, navigate]);

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const country = countryPhoneCodes.find((c) => c.name === countryName);

    setFormData((prev) => ({
      ...prev,
      country: countryName,
    }));

    if (country) {
      setSelectedCode(country.code);
    } else {
      // Reset to Indonesia if no country selected
      setSelectedCode("+62");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setSnackbar({
        open: true,
        message: getmessage("register.mustAgreeToTerms"),
        severity: "error",
      });
      return;
    }
    setLoading(true);
    const lang = localStorage.getItem("i18nextLng") || "en";

    try {
      const payload = {
        email: formData.email,
        fullName: formData.fullName,
        phoneCode: selectedCode,
        phone: formData.phone,
        country: formData.country,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/sign-up-by-google`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
          },
        }
      );

      const { data } = response;

      if (data.status === "success" && data.data?.token) {
        // Store the access token
        localStorage.setItem("token", data.data.token);

        // Store user data if needed
        const encrypted = encryptData(data.data.user);
        localStorage.setItem("user", encrypted);

        // Call the login function from AuthContext
        login(data.data.token, data.data.user);

        // Redirect to dashboard
        setSnackbar({
          open: true,
          message: response.data.message || "Registration successful!",
          severity: "success",
        });

        // Redirect to page
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        // Handle unexpected response format
        setSnackbar({
          open: true,
          message: "Registration successful but invalid response format",
          severity: "error",
        });
      }
      // Reset form after successful registration
      setFormData({
        fullName: "",
        email: "",
        country: "",
        phone: "",
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1, sm: 2 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ duration: 0.6 }}
        style={{ width: "100%" }}
      >
        <Paper
          elevation={0}
          sx={{
            backdropFilter: "blur(16px)",
            backgroundColor: "rgba(255, 255, 255, 0.92)",
            p: { xs: 2, sm: 5 },
            borderRadius: 4,
            width: "100%",
            maxWidth: { xs: "90%", sm: 480 },
            margin: "0 auto",
            textAlign: "center",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
              boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
            }}
          >
            <PersonAddAlt sx={{ fontSize: 30, color: "#fff" }} />
          </Box>

          <Typography variant="h4" fontWeight="bold" mb={1}>
            {t("signup.title")}
          </Typography>
          <Typography variant="h5" fontWeight="bold" mb={1}>
            {googleData?.email}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            {t("register.subtitle")}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label={t("register.fullNameLabel")}
                name="fullName"
                variant="outlined"
                fullWidth
                value={formData.fullName}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAddAlt color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />

              <Autocomplete
                id="country"
                options={countryPhoneCodes}
                getOptionLabel={(option) => option.name || ""}
                value={
                  countryPhoneCodes.find((c) => c.name === formData.country) ||
                  null
                }
                onChange={(event, newValue) => {
                  handleCountryChange({
                    target: {
                      name: "country",
                      value: newValue ? newValue.name : "",
                    },
                  });
                }}
                isOptionEqualToValue={(option, value) =>
                  option.name === value.name
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("register.countryLabel")}
                    name="country"
                    required
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PublicIcon color="action" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2, textAlign: "left" },
                    }}
                  />
                )}
              />

              <TextField
                label={t("register.phoneLabel")}
                name="phone"
                variant="outlined"
                type="number"
                fullWidth
                value={formData.phone}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                      <span style={{ marginLeft: 8 }}>{selectedCode}</span>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />

              {/* Checkbox Agreement */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    name="termsAgreement"
                    color="primary"
                  />
                }
                sx={{ mt: 2, alignItems: "center" }}
                label={
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t(
                      "register.agreePrefix",
                      "By signing up you agree to our"
                    )}
                    &nbsp;
                    <Link
                      href="/term-conditions"
                      target="_blank"
                      underline="hover"
                    >
                      {t("register.terms", "Terms")}
                    </Link>{" "}
                    &amp;&nbsp;
                    <Link
                      href="/privacy-policy"
                      target="_blank"
                      underline="hover"
                    >
                      {t("register.privacy", "Privacy Policy")}
                    </Link>
                  </Typography>
                }
              />

              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<PersonAddAlt />}
                  disabled={loading}
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    fontWeight: "bold",
                    textTransform: "none",
                    py: 1.5,
                    fontSize: "1rem",
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 24px ${theme.palette.primary.main}60`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("register.createAccountButton")
                  )}
                </Button>
              </motion.div>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                {t("register.haveAccountText")}{" "}
                <Button
                  href="/login"
                  variant="text"
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t("register.signInButton")}
                </Button>
              </Typography>
            </Stack>
          </form>
        </Paper>
      </motion.div>

      {/* Modern Snackbar for notifications */}
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default SignUp;
